import { useState } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { X, Upload, Loader } from 'lucide-react'
import { uploadHealedTattooPhoto } from '@/lib/tattooPhotoUpload'
import type { TattooTimelineEntry } from '@/types/models'

interface AddHealedPhotoDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  artistId: string
  clientId: string
  tattoo: TattooTimelineEntry | null
  currentUserId: string
  onSuccess?: () => void
}

export function AddHealedPhotoDialog({
  open,
  onOpenChange,
  artistId,
  clientId,
  tattoo,
  currentUserId,
  onSuccess
}: AddHealedPhotoDialogProps) {
  const [selectedFiles, setSelectedFiles] = useState<File[]>([])
  const [previewUrls, setPreviewUrls] = useState<string[]>([])
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [caption, setCaption] = useState('')

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || [])
    if (files.length === 0) return

    // Limit to 3 photos
    const newFiles = files.slice(0, 3 - selectedFiles.length)
    
    // Create preview URLs
    const newPreviewUrls = newFiles.map(file => URL.createObjectURL(file))
    
    setSelectedFiles(prev => [...prev, ...newFiles])
    setPreviewUrls(prev => [...prev, ...newPreviewUrls])
  }

  const removeImage = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index))
    setPreviewUrls(prev => {
      const newUrls = prev.filter((_, i) => i !== index)
      // Clean up object URL
      URL.revokeObjectURL(prev[index])
      return newUrls
    })
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (selectedFiles.length === 0 || !tattoo) {
      setError('Please select at least one photo')
      return
    }

    setUploading(true)
    setError(null)

    try {
      // Upload each file
      for (const file of selectedFiles) {
        await uploadHealedTattooPhoto(
          artistId,
          clientId,
          tattoo.id,
          file,
          currentUserId,
          caption.trim() || undefined
        )
      }

      // Success!
      onSuccess?.()
      handleClose()
    } catch (err) {
      console.error('Error adding healed photos:', err)
      setError(err instanceof Error ? err.message : 'Failed to add healed photos')
    } finally {
      setUploading(false)
    }
  }

  const handleClose = () => {
    // Clean up preview URLs
    previewUrls.forEach(url => {
      try {
        URL.revokeObjectURL(url)
      } catch (e) {
        // Ignore cleanup errors
      }
    })
    
    // Reset form
    setSelectedFiles([])
    setPreviewUrls([])
    setCaption('')
    setError(null)
    
    onOpenChange(false)
  }

  if (!tattoo) return null

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Add Healed Photos</DialogTitle>
          <DialogDescription>
            Show how this tattoo healed! Upload photos of the healed tattoo.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Photo upload */}
          <div>
            <Label>Photos (up to 3)</Label>
            
            {/* Preview grid */}
            {previewUrls.length > 0 && (
              <div className="grid grid-cols-3 gap-2 mb-3 mt-2">
                {previewUrls.map((url, index) => (
                  <div key={index} className="relative aspect-square rounded-lg overflow-hidden bg-muted">
                    <img
                      src={url}
                      alt={`Preview ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                    <button
                      type="button"
                      onClick={() => removeImage(index)}
                      className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/60 hover:bg-black/80 flex items-center justify-center transition-colors"
                    >
                      <X className="w-4 h-4 text-white" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Upload button */}
            {previewUrls.length < 3 && (
              <label className="flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-border rounded-lg cursor-pointer hover:border-primary hover:bg-muted/50 transition-colors">
                <Upload className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">
                  {previewUrls.length === 0 ? 'Select healed photos' : 'Add more photos'}
                </span>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleFileSelect}
                  className="hidden"
                />
              </label>
            )}
          </div>

          {/* Caption */}
          <div>
            <Label htmlFor="caption">Caption (optional)</Label>
            <Input
              id="caption"
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              placeholder="Fully healed after 8 weeks!"
            />
          </div>

          {/* Tattoo info preview */}
          {tattoo.description && (
            <div className="bg-muted p-3 rounded-lg">
              <p className="text-sm font-medium text-foreground mb-1">Adding healed photos to:</p>
              <p className="text-sm text-muted-foreground">{tattoo.description}</p>
            </div>
          )}

          {/* Error message */}
          {error && (
            <div className="bg-destructive/10 text-destructive text-sm p-3 rounded-lg">
              {error}
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={uploading}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={uploading || selectedFiles.length === 0}
              className="flex-1"
            >
              {uploading ? (
                <>
                  <Loader className="w-4 h-4 mr-2 animate-spin" />
                  Uploading...
                </>
              ) : (
                'Add Photos'
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}


